"use client";

import { StyledLink } from "@/components/StyledLink";
import {
  Box,
  Container,
  Typography,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Stack,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  styled,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandIcon as ExpandMore,
  ExternalLink,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import { useUser } from "@/contexts/UserContext";
import { useAlert } from "@/contexts/AlertContext";
import { apiRemote, IHost } from "@/api";
import { useWorkshop } from "@/contexts/WorkshopContext";

// Calendar type definition
type CalendarEvent = {
  id: number;
  date: string;
  title: string;
  description?: string; // Thêm description
  image_url?: string;  // Thêm image_url
  event_link?: string; // Thêm event_link
  video_link?: string; // Thêm video_link
  host_name?: string;       // Thêm host
  hosts?: IHost[];
  topics?: { id: number; name: string }[]; // Thêm topics
  is_registered?: boolean; // Thêm is_registered
};

const StyledFullCalendarContainer = styled(Box)(({ theme }) => ({
  "& .fc-event": {
    marginBottom: "4px", // Khoảng cách giữa các sự kiện
    borderRadius: "4px", // Bo tròn góc
    backgroundColor: theme.palette.primary.main, // Hoặc màu sắc mặc định của FullCalendar
    color: theme.palette.primary.contrastText,
    padding: "2px 4px", // Padding bên trong sự kiện
    fontSize: "0.85rem",
  },
  "& .fc-daygrid-event": {
    marginTop: "2px", // Có thể thêm margin-top nếu muốn
    marginBottom: "2px",
  },
  // Nếu muốn điều chỉnh kích thước text
  "& .fc-event-title": {
    whiteSpace: "normal", // Cho phép xuống dòng
    lineHeight: 1.2,
  }
}));

// Hàm xử lý dữ liệu host
const processWorkshopData = (workshop: any) => {
  let parsedHosts: IHost[] = [];
  if (workshop.hosts && typeof workshop.hosts === 'string' && workshop.hosts !== '[]') {
    try {
      parsedHosts = JSON.parse(workshop.hosts);
    } catch (e) {
      console.error("Failed to parse hosts string:", e);
    }
  } else if (Array.isArray(workshop.hosts)) {
    parsedHosts = workshop.hosts;
  }
  return {
    ...workshop,
    hosts: parsedHosts,
  };
};

// Hàm hiển thị host thân thiện
const renderHosts = (hosts: IHost[] | undefined, hostName: string | undefined) => {
  if (hosts && hosts.length > 0) {
    if (hosts.length === 1) {
      return hosts[0].host_name;
    }
    return `${hosts[0].host_name} and ${hosts.length - 1} other(s)`;
  }
  return hostName || 'N/A';
};

export default function EducatorSchedule() {
  const navigate = useNavigate();
  const { user } = useUser();
  const alert = useAlert();
  // const [loading, setLoading] = useState<boolean>(false);
  // const [registeredWorkshops, setRegisteredWorkshops] = useState<any[]>([]);
  const { registeredWorkshops, loadingWorkshops } = useWorkshop();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );

  const parseTopics = (topicString: string): { id: string; name: string }[] => {
    try {
      // FullCalendar có thể tự động chuyển đổi JSON string sang object nếu nó được cung cấp đúng
      // Tuy nhiên, để đảm bảo, chúng ta sẽ parse thủ công nếu cần.
      // Dữ liệu mẫu topic là một mảng chứa object có "id" và "name"
      const parsed = JSON.parse(topicString);
      if (Array.isArray(parsed)) {
        return parsed.map(item => ({
            id: String(item.id), // Đảm bảo id là string
            name: item.name
        }));
      }
      return [];
    } catch (e) {
      console.error("Failed to parse topics:", topicString, e);
      return [];
    }
  };

  const allCalendarEvents = [
    ...registeredWorkshops.map(workshop => {
      const processedWorkshop = processWorkshopData(workshop);
      return {
        id: processedWorkshop.id,
        title: processedWorkshop.title,
        date: processedWorkshop.date,
        description: processedWorkshop.description,
        image_url: processedWorkshop.image_url,
        event_link: processedWorkshop.event_link,
        video_link: processedWorkshop.video_recording_url,
        host_name: processedWorkshop.host_name,
        hosts: processedWorkshop.hosts, // Sử dụng hosts đã được xử lý
        topics: processedWorkshop.topic ? parseTopics(processedWorkshop.topic) : [],
        is_registered: true,
      };
    })
  ];

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Schedule & Events</Typography>
        {loadingWorkshops && <CircularProgress size={24} />}
      </Box>
      <StyledFullCalendarContainer>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,listWeek",
          }}
          events={allCalendarEvents.map((event) => ({
            title: event.title,
            start: event.date,
            extendedProps: event,
          }))}
          eventClick={(info) => {
            setSelectedEvent(info.event.extendedProps as CalendarEvent);
          }}
          eventContent={(arg) => (
            <div style={{ cursor: "pointer" }}>{arg.event.title}</div>
          )}
        />
      </StyledFullCalendarContainer>

      <Dialog open={!!selectedEvent} onClose={() => setSelectedEvent(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Workshop Details</DialogTitle>
        <DialogContent dividers>
          {selectedEvent && (
            <Box>
              {selectedEvent.image_url && (
                <Box sx={{ mb: 2, borderRadius: '4px', overflow: 'hidden' }}>
                  <img src={selectedEvent.image_url} alt={selectedEvent.title} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                </Box>
              )}
              <Typography variant="h6" gutterBottom>{selectedEvent.title}</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Date:</strong> {new Date(selectedEvent.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                {/* Hiển thị thời gian nếu có */}
                {new Date(selectedEvent.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Host:</strong> {renderHosts(selectedEvent.hosts, selectedEvent.host_name)}
              </Typography>
              {selectedEvent.description && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedEvent.description}
                </Typography>
              )}
              {selectedEvent.topics && selectedEvent.topics.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Topics:</Typography>
                  {selectedEvent.topics.map((topic) => (
                    <Chip key={topic.id} label={topic.name} size="small" sx={{ mr: 1, mb: 1, bgcolor: '#6f42c1', color: 'white' }} />
                  ))}
                </Box>
              )}
              {selectedEvent.event_link && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <Link href={selectedEvent.event_link} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'primary.main' }}>
                    <ExternalLink size={16} style={{ marginRight: 8 }} />
                    Live Event
                  </Link>
                </Typography>
              )}
              {selectedEvent.video_link && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Link href={selectedEvent.video_link} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'primary.main' }}>
                    <ExternalLink size={16} style={{ marginRight: 8 }} />
                    Watch Video
                  </Link>
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedEvent(null)} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
